"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { useGetWinnersListQuery } from "@/app/redux/features/api/winners";
import { hasActiveSubscription } from "@/helpers/hasActiveSubscription";
import CategoryTabs from "../../components/CategoryTabs";
import WinnerCard from "../../components/WinnerCard";
import {
  CATEGORY_ROUTE_PREFIX,
  filterWinnersByCategory,
  findCategoryBySlug,
  SUBSCRIBER_COMPETITION_KEY,
} from "../../category-utils";
import Loader from "../../loader";
import NoWinners from "../../noWinners";
import { getWinnerCardImageProps } from "../../winners-image-priority";
import useDeferredReviveAds from "@/app/hooks/useDeferredReviveAds";

const PAGE_SIZE = 12;

const readSlugFromPath = (): string => {
  if (typeof window === "undefined") return "";
  const match = window.location.pathname.match(new RegExp(`${CATEGORY_ROUTE_PREFIX}([^/]+)`));
  return match?.[1] ? decodeURIComponent(match[1]) : "";
};

const WinnersCategoryPage = () => {
  const [slug] = useState(readSlugFromPath);
  const { data, isLoading } = useGetWinnersListQuery();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const reviveAdBanner = useRef<HTMLDivElement>(null);

  const category = useMemo(
    () => findCategoryBySlug(data?.winnerCategories ?? [], slug),
    [data?.winnerCategories, slug]
  );

  const winners = useMemo(() => {
    if (!category || !data?.winnerList) return [];
    const subscriberCompetition = data.winnerCategories.find(c => c.Key === SUBSCRIBER_COMPETITION_KEY);
    return filterWinnersByCategory(data.winnerList, category.Key)
      .filter(w => w.SuppressResults !== "True")
      .map(item => ({
        ...item,
        isSubscriberCompetition: item.WinnerCategories?.includes(subscriberCompetition?.Key ?? ""),
      }));
  }, [category, data?.winnerList, data?.winnerCategories]);

  useDeferredReviveAds();

  useEffect(() => {
    const handleAdsLoaded = (e: any) => {
      const detail = e?.detail;
      if (!detail) return;
      const hasAdsToLoad = Object.values(detail).some((ad: any) => Number(ad.width) > 0 || Number(ad.height) > 0);

      if (hasAdsToLoad) {
        const adHeight = document.querySelector(".winner-mobile-ad")?.clientHeight || 100;

        const footer = document.querySelector(".footer-v2") as HTMLElement | null;
        if (footer) {
          if (window.innerWidth < 1024) {
            footer.style.paddingBottom = `${adHeight}px`;
          } else {
            footer.style.paddingBottom = "";
          }
        }
      }
    };

    const handleResize = () => {
      const footer = document.querySelector(".footer-v2") as HTMLElement | null;
      if (!footer) return;

      if (window.innerWidth < 1024) {
        const adHeight = document.querySelector(".winner-mobile-ad")?.clientHeight || 100;
        footer.style.paddingBottom = `${adHeight}px`;
      } else {
        footer.style.paddingBottom = "";
      }
    };
    document.addEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", handleAdsLoaded);
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      document.removeEventListener("revive-6ee315d200a003a5ac39b07d0dd34156-completed", handleAdsLoaded);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const visibleWinners = winners.slice(0, visibleCount);
  const hasMore = visibleCount < winners.length;

  return (
    <>
      <div id={"script_ad_server"} className={"hidden"} />
      <div ref={reviveAdBanner} className={"hidden"} />

      {isLoading ? (
        <Loader />
      ) : !category ? (
        <>
          <div className="lg:hidden h-12" aria-hidden />

          <div
            className="bg-white drop-shadow-[0px_1.5px_2px_rgba(0,0,0,0.1)] max-lg:fixed max-lg:left-0 max-lg:right-0 max-lg:z-40 lg:sticky lg:z-40 lg:mt-7"
            style={{ top: "var(--header-height)" }}>
            <div className="relative lg:px-8">
              <div className="w-full max-w-[1360px] mx-auto">
                <CategoryTabs categories={data?.winnerCategories ?? []} />
              </div>
            </div>
          </div>

          <NoWinners />
        </>
      ) : (
        <>
          <section className="mx-auto mt-5 pt-12 lg:pt-0 mainSection">
            <div className="flex flex-col items-start lg:items-center gap-2 lg:gap-3 px-3 text-left lg:text-center">
              <h1
                data-testid="winners-category-title"
                className="!mb-0 normal-case !text-left lg:!text-center text-dark-1 font-jost text-xl lg:text-[32px] font-semibold leading-[26px] lg:leading-10">
                Meet Our {category.Value} Winners
              </h1>

              <p className="tracking-[0] max-w-xl text-gray-1 font-jost text-sm lg:text-base font-normal leading-5 lg:leading-6">
                Every week, lucky winners take home incredible prizes. Your story could be next.
              </p>
            </div>
          </section>

          {/* Category menu: mobile fixed below header, desktop sticky below header on scroll */}
          <div
            className="bg-white drop-shadow-[0px_1.5px_2px_rgba(0,0,0,0.1)] max-lg:fixed max-lg:left-0 max-lg:right-0 max-lg:z-40 lg:sticky lg:z-40 lg:mt-7"
            style={{ top: "var(--header-height)" }}>
            <div className="relative lg:px-8">
              <div className="w-full max-w-[1360px] mx-auto">
                <CategoryTabs categories={data?.winnerCategories ?? []} />
              </div>
            </div>
          </div>

          {winners.length === 0 ? (
            <NoWinners />
          ) : (
            <div className="lg:px-8 mt-6 lg:mt-0 py-6 lg:py-10 bg-gray-4">
              <section className="mx-auto mainSection">
                <div className="flex flex-col gap-8">
                  {/* mobile wide */}
                  <ins
                    data-revive-usersubscriber={hasActiveSubscription()}
                    data-revive-zoneid="8"
                    data-revive-source="WL_wide01_mob"
                    data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                    className={
                      "lg:hidden [&:empty]:hidden fixed-theme z-[20] bottom-0 inset-x-0 winner-mobile-ad"
                    }></ins>

                  {/* desktop wide */}
                  <ins
                    data-revive-usersubscriber={hasActiveSubscription()}
                    data-revive-zoneid="7"
                    data-revive-source="WL_wide01_desk"
                    data-revive-id="6ee315d200a003a5ac39b07d0dd34156"
                    className={"max-lg:hidden [&:empty]:hidden block winner-desktop-ad"}></ins>

                  <div className="px-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                      {visibleWinners.map(winner => (
                        <WinnerCard
                          key={winner.CompetitionRef || winner.Url}
                          winner={winner}
                          {...getWinnerCardImageProps(false)}
                        />
                      ))}
                    </div>

                    <div className="flex flex-col items-center mt-8 lg:mt-12">
                      {hasMore && (
                        <button
                          type="button"
                          onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                          className="flex items-center justify-center gap-2 w-full max-w-[240px] lg:max-w-[280px] h-14 px-5 text-base font-medium font-jost leading-[22px] text-primary bg-transparent border border-primary rounded">
                          Load more winners
                          <svg
                            className="shrink-0 size-6"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true">
                            <path
                              d="M9 6L15 12L9 18"
                              stroke="currentColor"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default WinnersCategoryPage;
